import { useMemo, useState } from "react";
import { MessageCircle, Calculator as CalcIcon } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { CALC_ITEMS, formatIDR, waLink } from "@/data/site";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-white/50";

export const Calculator = () => {
  const [itemId, setItemId] = useState(CALC_ITEMS[0].id);
  const [qty, setQty] = useState(10);
  const [p, setP] = useState(400);
  const [l, setL] = useState(10);
  const [t, setT] = useState(5);

  const item = CALC_ITEMS.find((i) => i.id === itemId);

  const result = useMemo(() => {
    if (item.mode === "sheet") {
      const volume = qty * 1.22 * 2.44 * 0.018;
      return { volume, total: qty * item.price, label: `${qty} lembar` };
    }
    const volume = (p / 100) * (l / 100) * (t / 100) * qty;
    return { volume, total: volume * item.price, label: `${qty} batang (${p}x${l}x${t} cm)` };
  }, [item, qty, p, l, t]);

  const waMsg = `Halo PT. SINAR AL MUBAROKAH, saya ingin penawaran untuk: ${item.name}, jumlah ${result.label}, estimasi volume ${result.volume.toFixed(3)} m3, estimasi total ${formatIDR(result.total)}. Mohon harga terbaiknya.`;

  return (
    <section
      id="kalkulator"
      data-testid="calculator-section"
      className="grain relative overflow-hidden py-24 lg:py-32"
      style={{ backgroundColor: "var(--t-dark)" }}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <SectionHead
          dark
          overline="Kalkulator Kayu"
          title={<>Hitung Volume & <span className="italic" style={{ color: "var(--t-accent)" }}>Estimasi Harga</span></>}
          sub="Estimasi instan kebutuhan material Anda. Kirim rincian langsung ke tim sales kami untuk harga grosir terbaik."
        />
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-[1.6rem] border border-white/10 bg-white/5 p-7 backdrop-blur-sm">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/60">Jenis Produk</label>
              <select
                value={itemId}
                onChange={(e) => setItemId(e.target.value)}
                data-testid="calc-product-select"
                className={inputCls}
              >
                {CALC_ITEMS.map((i) => (
                  <option key={i.id} value={i.id} className="text-black">
                    {i.name}
                  </option>
                ))}
              </select>

              <label className="mb-2 mt-5 block text-xs font-semibold uppercase tracking-wider text-white/60">
                Jumlah ({item.mode === "sheet" ? "lembar" : "batang"})
              </label>
              <input
                type="number"
                min="1"
                value={qty}
                onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                data-testid="calc-qty-input"
                className={inputCls}
              />

              {item.mode === "volume" && (
                <div className="mt-5 grid grid-cols-3 gap-4">
                  {[["Panjang (cm)", p, setP, "calc-length"], ["Lebar (cm)", l, setL, "calc-width"], ["Tebal (cm)", t, setT, "calc-thick"]].map(([label, val, setter, tid]) => (
                    <div key={tid}>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-white/60">{label}</label>
                      <input
                        type="number"
                        min="1"
                        value={val}
                        onChange={(e) => setter(Math.max(1, Number(e.target.value) || 1))}
                        data-testid={tid}
                        className={inputCls}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              className="flex h-full flex-col justify-between rounded-[1.6rem] border p-8"
              style={{ borderColor: "color-mix(in srgb, var(--t-accent) 35%, transparent)", backgroundColor: "color-mix(in srgb, var(--t-accent) 8%, transparent)" }}
            >
              <div>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--t-accent)" }}>
                  <CalcIcon size={15} /> Hasil Estimasi
                </p>
                <div className="mt-6 grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/55">Volume Total</p>
                    <p className="mt-1 font-serif text-4xl font-semibold text-white" data-testid="calc-volume-result">
                      {result.volume.toFixed(3)} <span className="text-lg">m3</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/55">Estimasi Harga</p>
                    <p className="mt-1 font-serif text-3xl font-semibold sm:text-4xl" style={{ color: "var(--t-accent)" }} data-testid="calc-price-result">
                      {formatIDR(result.total)}
                    </p>
                  </div>
                </div>
                <p className="mt-5 text-xs leading-relaxed text-white/50">
                  *Estimasi berdasarkan harga pasar. Harga grosir final bisa lebih rendah — hubungi tim sales kami.
                </p>
              </div>
              <a
                href={waLink(waMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calc-whatsapp-button"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-bold transition-transform duration-300 hover:-translate-y-0.5"
                style={{ backgroundColor: "var(--t-accent)", color: "var(--t-dark)" }}
              >
                <MessageCircle size={17} /> Kirim Rincian ke WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
